import React, { useMemo, useState } from 'react';
import { alpha, Box, IconButton, Paper, Stack, Tooltip, Typography, useTheme, type Theme } from '@mui/material';
import { ArrowDownward, ArrowUpward, KeyboardArrowRight, Star, StarBorder } from '@mui/icons-material';
import { SimDriverResult } from '../../services/api';
import { getCellColor } from '../../utils/cellColors';
import { positionCellSx, probCellColor, strongCellColor, tableBgFor, vivid } from '../../utils/simColors';
import { SegmentedToggle } from '../SegmentedToggle';
import { lastName, useChartTokens, useWidth } from './chartKit';

const quantile = (dist: number[], q: number) => {
  let c = 0;
  for (let i = 0; i < dist.length; i++) if ((c += dist[i]) >= q) return i + 1;
  return dist.length;
};
const bustOf = (d: SimDriverResult) => (d.finishDist ?? []).slice(20).reduce((a, b) => a + b, 0);

/** Natural breaks (Jenks) on sorted values: k groups, each as tight as possible. */
const naturalBreaks = (v: number[], k: number): number[] => {
  const n = v.length;
  const pre = [0];
  const pre2 = [0];
  v.forEach((x) => {
    pre.push(pre[pre.length - 1] + x);
    pre2.push(pre2[pre2.length - 1] + x * x);
  });
  const cost = (i: number, j: number) => {
    const m = j - i + 1;
    const s = pre[j + 1] - pre[i];
    return pre2[j + 1] - pre2[i] - (s * s) / m;
  };
  const D = Array.from({ length: k + 1 }, () => Array(n).fill(Infinity));
  const B = Array.from({ length: k + 1 }, () => Array(n).fill(0));
  for (let j = 0; j < n; j++) D[1][j] = cost(0, j);
  for (let g = 2; g <= k; g++)
    for (let j = g - 1; j < n; j++)
      for (let i = g - 1; i <= j; i++) {
        const c = D[g - 1][i - 1] + cost(i, j);
        if (c < D[g][j]) {
          D[g][j] = c;
          B[g][j] = i;
        }
      }
  const out = Array(n).fill(0);
  let j = n - 1;
  for (let g = k; g >= 1; g--) {
    const i = g > 1 ? B[g][j] : 0;
    for (let x = i; x <= j; x++) out[x] = g - 1;
    j = i - 1;
  }
  return out;
};

export interface FieldRow {
  d: SimDriverResult;
  q: [number, number, number, number, number];
  bust: number;
  tier: number;
  tierColor: string;
}

/** Tier colors: six distinct steps from green (best) to red (worst), so neighboring tiers never look alike (a smooth
 * gradient made tiers 1 and 2 both read green). */
const TIER_STEPS = ['#3FB868', '#A3CC45', '#EFCB45', '#F39C45', '#EC6B55', '#D9475C'];

export const useFieldRows = (drivers: SimDriverResult[]) => {
  const theme = useTheme();
  return useMemo(() => {
    const sorted = [...drivers].filter((d) => d.finishDist?.length).sort((a, b) => a.avgFinish - b.avgFinish);
    const tiers = naturalBreaks(
      sorted.map((d) => d.avgFinish),
      Math.min(6, sorted.length),
    );
    const nTiers = Math.max(...tiers) + 1;
    return sorted.map((d, i): FieldRow => {
      const dist = d.finishDist!;
      // One clear step per tier, best green through worst red (the table's scale, in distinct steps).
      const step = TIER_STEPS[nTiers > 1 ? Math.round((tiers[i] * (TIER_STEPS.length - 1)) / (nTiers - 1)) : 0];
      return {
        d,
        q: [quantile(dist, 0.1), quantile(dist, 0.25), quantile(dist, 0.5), quantile(dist, 0.75), quantile(dist, 0.9)],
        bust: bustOf(d),
        tier: tiers[i],
        tierColor: strongCellColor(theme, step),
      };
    });
  }, [drivers, theme]);
};

/** The boom vs bust map's drivers (the likeliest front-runners) and its quadrant lines (their averages). */
export const MAP_DRIVERS = 24;
export const mapAverages = (rows: FieldRow[]) => {
  const top = rows.slice(0, MAP_DRIVERS);
  return {
    top,
    boom: top.reduce((a, r) => a + r.d.top5Prob, 0) / top.length,
    bust: top.reduce((a, r) => a + r.bust, 0) / top.length,
  };
};

/** Where a driver lands on the boom vs bust map; null when they're not on it. */
export const mapQuadrant = (rows: FieldRow[], driver: string) => {
  const m = mapAverages(rows);
  const r = m.top.find((x) => x.d.driver === driver);
  if (!r) return null;
  const boom = r.d.top5Prob >= m.boom;
  const safe = r.bust <= m.bust;
  return boom && safe
    ? {
        label: 'Steady contender',
        tone: 'success' as const,
        note: 'more top 5s and fewer busts than most front-runners',
      }
    : boom
      ? {
          label: 'Boom or bust',
          tone: 'warning' as const,
          note: 'plenty of top 5s, but busts more than most front-runners',
        }
      : safe
        ? {
            label: 'Safe but quiet',
            tone: 'info' as const,
            note: 'rarely busts, but fewer top 5s than most front-runners',
          }
        : { label: 'Long shot', tone: 'default' as const, note: 'fewer top 5s and more busts than most front-runners' };
};

export const pctText = (p: number) => (p >= 0.1 ? `${Math.round(p * 100)}%` : `${(p * 100).toFixed(1)}%`);
export const ordText = (n: number) => {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${n}${s}`;
};

type BoomView = 'range' | 'map';
type BoomSortKey = 'avg' | 'boom' | 'bust';

/** The page's pills for a driver (track rank, streak), shown under the name like the table's; null when none. */
export type DriverPills = (driver: string, compact: boolean) => React.ReactNode;

const NUM_W = 96; // a number column (the table's probability columns are 104)
const STAR_W = 40;

/** Average finish in the table's red-yellow-green position colors: full strength when sorted, dimmed otherwise. */
const posTextColor = (theme: Theme, avg: number, strong?: boolean) => {
  const c = strongCellColor(theme, vivid(getCellColor(avg, 'position')));
  return strong ? c : alpha(c, theme.palette.mode === 'dark' ? 0.6 : 0.85);
};

/** The finish axis' labels (1st … last), placed like the bars below them. */
const AxisTicks: React.FC<{ maxPos: number }> = ({ maxPos }) => {
  const ticks = [1, 10, 20, 30, ...(maxPos >= 34 ? [maxPos] : [])].filter((t) => t <= maxPos);
  return (
    <Box sx={{ position: 'relative', height: 14 }}>
      {ticks.map((t) => (
        <Typography
          key={t}
          component="span"
          sx={{
            position: 'absolute',
            left: `${((t - 1) / (maxPos - 1)) * 100}%`,
            transform: t === 1 ? 'none' : t === maxPos ? 'translateX(-100%)' : 'translateX(-50%)',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: 'text.secondary',
            lineHeight: '14px',
          }}
        >
          {t === 1 ? '1st' : t}
        </Typography>
      ))}
    </Box>
  );
};

/**
 * One driver's simulated finishes on the field's axis: thin line 10th–90th percentile, thick bar the middle half, dot
 * at the average finish. Boom or bust shades the top-5 (green) and 21st-or-worse (red) zones behind it.
 */
const RangeBar: React.FC<{ r: FieldRow; maxPos: number; color: string; zones?: boolean; height: number }> = ({
  r,
  maxPos,
  color,
  zones,
  height,
}) => {
  const theme = useTheme();
  const x = (p: number) => `${((Math.min(maxPos, Math.max(1, p)) - 1) / (maxPos - 1)) * 100}%`;
  const span = (a: number, b: number) => ({ left: x(a), width: `calc(${x(b)} - ${x(a)})` });
  const [p10, p25, , p75, p90] = r.q;
  const mid = height / 2;
  return (
    <Tooltip
      title={`Half their finishes: ${ordText(p25)}–${ordText(p75)} · 8 in 10: ${ordText(p10)}–${ordText(p90)}`}
      placement="top"
    >
      <Box
        sx={{
          position: 'relative',
          height,
          // Hovering the driver's row thickens their marks (as the old chart did); hovering the bar itself more so.
          '& .rb-thick, & .rb-thin, & .rb-dot': { transition: 'transform 0.15s ease, box-shadow 0.15s ease' },
          '.fc-row:hover & .rb-thick': { transform: 'scaleY(1.4)' },
          '.fc-row:hover & .rb-thin': { transform: 'scaleY(1.5)' },
          '.fc-row:hover & .rb-dot': { transform: 'scale(1.15)' },
          '&:hover .rb-thick': {
            transform: 'scaleY(1.75)',
            boxShadow: `0 0 0 1.5px ${alpha(theme.palette.text.primary, 0.35)}`,
          },
          '&:hover .rb-thin': { transform: 'scaleY(2)' },
          '&:hover .rb-dot': { transform: 'scale(1.3)' },
        }}
      >
        {zones && (
          <>
            <Box
              sx={{ position: 'absolute', top: 0, bottom: 0, ...span(1, 5.5), borderRadius: 1, bgcolor: alpha(theme.palette.success.main, 0.1) }}
            />
            <Box
              sx={{ position: 'absolute', top: 0, bottom: 0, ...span(20.5, maxPos), borderRadius: 1, bgcolor: alpha(theme.palette.error.main, 0.07) }}
            />
          </>
        )}
        {[10, 20, 30]
          .filter((t) => t < maxPos)
          .map((t) => (
            <Box key={t} sx={{ position: 'absolute', top: 0, bottom: 0, left: x(t), width: '1px', bgcolor: 'divider' }} />
          ))}
        <Box className="rb-thin" sx={{ position: 'absolute', top: mid - 1, height: 2, ...span(p10, p90), bgcolor: alpha(color, 0.55) }} />
        <Box className="rb-thick" sx={{ position: 'absolute', top: mid - 4, height: 8, ...span(p25, p75), borderRadius: 4, bgcolor: color }} />
        <Box
          className="rb-dot"
          sx={{
            position: 'absolute',
            top: mid - 6,
            left: `calc(${x(r.d.avgFinish)} - 6px)`,
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: 'text.primary',
            border: 2,
            borderColor: 'background.paper',
          }}
        />
      </Box>
    </Tooltip>
  );
};

/** The table's number cell: colored value (bigger on the sort column) over a thin bar. */
const NumCell: React.FC<{ text: string; frac: number; color: string; strong?: boolean }> = ({ text, frac, color, strong }) => (
  <Box sx={{ width: NUM_W, flex: 'none', px: 0.75, fontVariantNumeric: 'tabular-nums' }}>
    <Typography sx={{ fontSize: strong ? 16 : 14.5, fontWeight: strong ? 800 : 600, color, lineHeight: 1.3 }}>{text}</Typography>
    <Box sx={{ mt: 0.4, height: 4, width: '80%', borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}>
      <Box sx={{ width: `${Math.max(2, Math.min(1, frac) * 100)}%`, height: '100%', borderRadius: 2, bgcolor: color }} />
    </Box>
  </Box>
);

/** The phone card's small stat tile (the phone table's look). */
const Tile: React.FC<{ text: string; label: string; color?: string; sx?: object }> = ({ text, label, color, sx }) => (
  <Box sx={{ flex: 1, textAlign: 'center', borderRadius: 1.5, py: 0.75, bgcolor: 'action.hover', ...sx }}>
    <Typography sx={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap', color }}>
      {text}
      <Box component="span" sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', ml: 0.25 }}>
        {label}
      </Box>
    </Typography>
  </Box>
);

/**
 * The Simulator's Tiers and Boom or bust tabs, laid out like the results table: one row per driver (rank rail, star,
 * name with Proj and pills), each driver's range of simulated finishes as a column (dot = average, thick bar = middle
 * half, thin = 8 in 10), then number columns. Tiers groups the rows into natural-break tiers; Boom or bust sorts by
 * boom (top 5), bust (21st or worse) or average finish, and has a boom vs bust map. Phones get the phone table's
 * cards. Clicking a driver picks them (the driver panel).
 */
export const FieldCharts: React.FC<{
  view: 'tiers' | 'boom';
  drivers: SimDriverResult[];
  /** The toolbar's search / favorites: drivers not in it are faded. */
  visible: SimDriverResult[];
  selected: string | null;
  onSelect: (driver: string) => void;
  /** Starred drivers (the table's favorites, also the Compare set); the star beside each name toggles them. */
  favorites: Set<string>;
  onToggleFavorite: (driver: string) => void;
  pills?: DriverPills;
  /** Phones: compact rows instead of cards (the phone table's cards / table switch): Boom or bust's outcome bars,
   * Tiers' one-line ranges. */
  phoneTable?: boolean;
}> = ({ view, drivers, visible, selected, onSelect, favorites, onToggleFavorite, pills, phoneTable }) => {
  const theme = useTheme();
  const tokens = useChartTokens();
  const { ref, width } = useWidth(320, 760);
  const rows = useFieldRows(drivers);
  const [hover, setHover] = useState<string | null>(null);
  const [boomView, setBoomView] = useState<BoomView>('range');
  // Boom or bust's row order: by boom chance (most first) unless a column header is clicked; clicking the sorted
  // column again flips it.
  const [boomSort, setBoomSort] = useState<{ key: BoomSortKey; asc: boolean }>({ key: 'boom', asc: false });
  const sortBy = (key: BoomSortKey) =>
    setBoomSort((cur) => (cur.key === key ? { key, asc: !cur.asc } : { key, asc: key !== 'boom' }));
  const shown = new Set(visible.map((d) => d.driver));
  const narrow = width < 560;
  const tableBg = tableBgFor(theme);
  const isMap = view === 'boom' && boomView === 'map';
  const phoneBars = narrow && view === 'boom' && !isMap && !!phoneTable;
  const phoneTierRows = narrow && view === 'tiers' && !!phoneTable;
  // Starred drivers get the table's faint tint, so they stand out wherever they sort to.
  const favTint = theme.palette.mode === 'dark' ? 'rgba(98, 191, 123, 0.07)' : 'rgba(46, 125, 50, 0.05)';
  const rangeGray = theme.palette.mode === 'dark' ? '#c9ccd1' : '#6b7178';

  const sortVal = (r: FieldRow) => (boomSort.key === 'avg' ? r.d.avgFinish : boomSort.key === 'boom' ? r.d.top5Prob : r.bust);
  // Boom or bust lists drivers in the chosen sort; Tiers keeps the tier order (average finish).
  const list =
    view === 'boom' ? [...rows].sort((a, b) => (boomSort.asc ? sortVal(a) - sortVal(b) : sortVal(b) - sortVal(a))) : rows;
  const maxPos = Math.max(...rows.map((r) => r.d.finishDist!.length));
  const maxBoom = Math.max(...rows.map((r) => r.d.top5Prob)) || 1;
  const maxBust = Math.max(...rows.map((r) => r.bust)) || 1;
  const railOf = (r: FieldRow) =>
    view === 'tiers' ? r.tierColor : strongCellColor(theme, vivid(getCellColor(r.d.projectedRank, 'position')));

  // The number columns: Boom or bust's three (sortable), or Tiers' average finish.
  const numbers = (r: FieldRow) =>
    view === 'tiers'
      ? [{ k: 'avg' as BoomSortKey, text: r.d.avgFinish.toFixed(1), frac: 1 - (r.d.avgFinish - 1) / (maxPos - 1), color: posTextColor(theme, r.d.avgFinish, true), strong: true }]
      : [
          { k: 'boom' as BoomSortKey, text: pctText(r.d.top5Prob), frac: r.d.top5Prob / maxBoom, color: probCellColor(theme, r.d.top5Prob, boomSort.key === 'boom'), strong: boomSort.key === 'boom' },
          { k: 'bust' as BoomSortKey, text: pctText(r.bust), frac: r.bust / maxBust, color: probCellColor(theme, 1 - r.bust, boomSort.key === 'bust'), strong: boomSort.key === 'bust' },
          { k: 'avg' as BoomSortKey, text: r.d.avgFinish.toFixed(1), frac: 1 - (r.d.avgFinish - 1) / (maxPos - 1), color: posTextColor(theme, r.d.avgFinish, boomSort.key === 'avg'), strong: boomSort.key === 'avg' },
        ];
  const nameW = width >= 860 ? 290 : 230;

  const toolbar = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 1, sm: 2 }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      sx={narrow && !isMap && !phoneBars && !phoneTierRows ? { pb: 1.5 } : { px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
    >
      {view === 'boom' && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <SegmentedToggle
            size="small"
            aria-label="Boom or bust view"
            value={boomView}
            onChange={setBoomView}
            options={[
              { value: 'range', label: 'Ranges' },
              { value: 'map', label: 'Map' },
            ]}
          />
          {narrow && !isMap && (
            <>
              <Box sx={{ flex: 1 }} />
              <SegmentedToggle
                size="small"
                aria-label="Sort by"
                value={boomSort.key}
                onChange={(key) => setBoomSort({ key, asc: key !== 'boom' })}
                options={[
                  { value: 'boom', label: 'Boom' },
                  { value: 'bust', label: 'Bust' },
                  { value: 'avg', label: 'Avg' },
                ]}
              />
            </>
          )}
        </Stack>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
        {phoneBars
          ? 'Each bar: the chance of a top 5 (green), 6th to 20th (gray) and 21st or worse (red). Avg: average simulated finish.'
          : isMap
          ? `The ${MAP_DRIVERS} drivers most likely to run up front. Dashed lines are their averages; dot colors are the tiers.`
          : view === 'tiers'
            ? 'Dot: average finish. Thick bar: half of simulated finishes; thin: 8 in 10. Tiers group drivers where the field naturally breaks apart.'
            : `Boom: a top-5 finish. Bust: 21st or worse.${narrow ? '' : ' Click Boom, Bust or Avg to sort.'}`}
      </Typography>
    </Stack>
  );

  // ---- desktop: table rows ----
  const headCell = (label: string, opts: { w?: number; k?: BoomSortKey } = {}) => {
    const sortable = view === 'boom' && opts.k && !isMap;
    const active = view === 'tiers' ? opts.k === 'avg' : !isMap && opts.k === boomSort.key;
    return (
      <Box
        key={label}
        role={sortable ? 'button' : undefined}
        aria-label={sortable ? `Sort by ${opts.k === 'avg' ? 'average finish' : opts.k}` : undefined}
        onClick={sortable ? () => sortBy(opts.k!) : undefined}
        sx={{
          width: opts.w,
          flex: 'none',
          px: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          cursor: sortable ? 'pointer' : undefined,
          textTransform: 'uppercase',
          fontSize: 11,
          letterSpacing: '0.06em',
          fontWeight: 700,
          color: active ? 'primary.main' : 'text.secondary',
          userSelect: 'none',
        }}
      >
        {label}
        {active && view === 'boom' && (boomSort.asc ? <ArrowUpward sx={{ fontSize: 14 }} /> : <ArrowDownward sx={{ fontSize: 14 }} />)}
      </Box>
    );
  };
  const headerRow = (
    <Stack direction="row" alignItems="center" sx={{ height: 40, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ width: STAR_W, flex: 'none' }} />
      {headCell('Driver', { w: nameW })}
      <Box sx={{ flex: 1, minWidth: 0, mx: 1.5 }}>
        <AxisTicks maxPos={maxPos} />
      </Box>
      {view === 'tiers'
        ? headCell('Avg', { w: NUM_W, k: 'avg' })
        : [headCell('Boom', { w: NUM_W, k: 'boom' }), headCell('Bust', { w: NUM_W, k: 'bust' }), headCell('Avg', { w: NUM_W, k: 'avg' })]}
    </Stack>
  );
  const star = (r: FieldRow, size: number) => {
    const fav = favorites.has(r.d.driver);
    return (
      <IconButton
        size="small"
        className={fav ? undefined : 'fav-star'}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(r.d.driver);
        }}
        aria-label={fav ? `Unstar ${r.d.driver}` : `Star ${r.d.driver}`}
        sx={{ p: 0, width: size + 4, height: size + 4 }}
      >
        {fav ? <Star sx={{ fontSize: size, color: 'warning.main' }} /> : <StarBorder sx={{ fontSize: size, color: 'text.disabled' }} />}
      </IconButton>
    );
  };
  const desktopRow = (r: FieldRow) => {
    const picked = selected === r.d.driver;
    const p = pills?.(r.d.driver, false);
    return (
      <Stack
        key={r.d.driver}
        className="fc-row"
        direction="row"
        alignItems="center"
        onClick={() => onSelect(r.d.driver)}
        sx={{
          position: 'relative',
          minHeight: 56,
          borderBottom: 1,
          borderColor: 'divider',
          cursor: 'pointer',
          opacity: shown.has(r.d.driver) ? 1 : 0.3,
          bgcolor: picked ? alpha(theme.palette.primary.main, 0.14) : favorites.has(r.d.driver) ? favTint : undefined,
          '&:hover': { bgcolor: picked ? alpha(theme.palette.primary.main, 0.18) : 'action.hover' },
          // Like the table: unstarred stars show on row hover (always on touch screens, which can't hover).
          '& .fav-star': { opacity: 0, transition: 'opacity 0.15s' },
          '&:hover .fav-star, & .fav-star:focus-visible': { opacity: 1 },
          '@media (hover: none)': { '& .fav-star': { opacity: 1 } },
        }}
      >
        <Box sx={{ position: 'absolute', left: 0, top: 7, bottom: 7, width: 3, borderRadius: '0 2px 2px 0', bgcolor: railOf(r) }} />
        <Box sx={{ width: STAR_W, flex: 'none', pl: '10px' }}>{star(r, 20)}</Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: nameW, flex: 'none', minWidth: 0, pl: 0.5 }}>
          <KeyboardArrowRight fontSize="small" sx={{ color: 'text.secondary' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>
              {r.d.driver}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden' }}>
              <span>Proj {r.d.projectedRank}</span>
              {p}
            </Stack>
          </Box>
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0, mx: 1.5 }}>
          <RangeBar r={r} maxPos={maxPos} color={view === 'tiers' ? r.tierColor : rangeGray} zones={view === 'boom'} height={36} />
        </Box>
        {numbers(r).map((n) => (
          <NumCell key={n.k} text={n.text} frac={n.frac} color={n.color} strong={n.strong} />
        ))}
      </Stack>
    );
  };
  const tierHeader = (t: number, tr: FieldRow[], phone: boolean) => {
    const c = tr[0].tierColor;
    const range = `${tr[0].d.avgFinish.toFixed(1)}–${tr[tr.length - 1].d.avgFinish.toFixed(1)}`;
    return (
      <Stack
        key={`tier${t}`}
        direction="row"
        alignItems="baseline"
        spacing={1.25}
        sx={
          phone
            ? { pl: 1.25, mt: t ? 1 : 0, borderLeft: `4px solid ${c}` }
            : { px: 2, py: 0.9, bgcolor: alpha(c, 0.1), borderBottom: 1, borderColor: 'divider', boxShadow: `inset 4px 0 0 ${c}` }
        }
      >
        <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', color: c }}>TIER {t + 1}</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {tr.length} driver{tr.length === 1 ? '' : 's'} · {phone ? 'avg' : 'average finish'} {range}
        </Typography>
      </Stack>
    );
  };
  const tierGroups = () => {
    const out: FieldRow[][] = [];
    list.forEach((r) => (out[r.tier] = [...(out[r.tier] ?? []), r]));
    return out.map((tr, t) => ({ t, tr })).filter((g) => g.tr?.length);
  };
  const table = (
    <Box>
      {headerRow}
      {view === 'tiers'
        ? tierGroups().map(({ t, tr }) => (
            <React.Fragment key={t}>
              {tierHeader(t, tr, false)}
              {tr.map(desktopRow)}
            </React.Fragment>
          ))
        : list.map(desktopRow)}
    </Box>
  );

  // ---- phones: the phone table's cards ----
  const card = (r: FieldRow) => {
    const picked = selected === r.d.driver;
    const p = pills?.(r.d.driver, true);
    const head =
      view === 'tiers'
        ? { text: r.d.avgFinish.toFixed(1), label: 'AVG FINISH', color: posTextColor(theme, r.d.avgFinish, true) }
        : boomSort.key === 'bust'
          ? { text: pctText(r.bust), label: 'BUST', color: probCellColor(theme, 1 - r.bust, true) }
          : { text: pctText(r.d.top5Prob), label: 'BOOM', color: probCellColor(theme, r.d.top5Prob, true) };
    const avgTile = <Tile key="avg" text={r.d.avgFinish.toFixed(1)} label="AVG" sx={positionCellSx(r.d.avgFinish, theme.palette.mode)} />;
    const tiles =
      view === 'tiers'
        ? [
            <Tile key="w" text={pctText(r.d.winProb)} label="WIN" color={probCellColor(theme, r.d.winProb)} />,
            <Tile key="5" text={pctText(r.d.top5Prob)} label="T5" color={probCellColor(theme, r.d.top5Prob)} />,
            <Tile key="10" text={pctText(r.d.top10Prob)} label="T10" color={probCellColor(theme, r.d.top10Prob)} />,
          ]
        : [
            boomSort.key === 'bust' ? (
              <Tile key="b" text={pctText(r.d.top5Prob)} label="BOOM" color={probCellColor(theme, r.d.top5Prob)} />
            ) : (
              <Tile key="b" text={pctText(r.bust)} label="BUST" color={probCellColor(theme, 1 - r.bust)} />
            ),
            <Tile key="10" text={pctText(r.d.top10Prob)} label="T10" color={probCellColor(theme, r.d.top10Prob)} />,
            avgTile,
          ];
    return (
      <Paper
        key={r.d.driver}
        className="fc-row"
        variant="outlined"
        onClick={() => onSelect(r.d.driver)}
        sx={{
          p: 1.75,
          pl: 2.5,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          opacity: shown.has(r.d.driver) ? 1 : 0.3,
          bgcolor: picked ? alpha(theme.palette.primary.main, 0.14) : favorites.has(r.d.driver) ? favTint : undefined,
        }}
      >
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: railOf(r) }} />
        <Stack direction="row" alignItems="center" spacing={1.25}>
          {star(r, 22)}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>
              {r.d.driver}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Proj {r.d.projectedRank}</Typography>
            {p && <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>{p}</Stack>}
          </Box>
          <Box sx={{ textAlign: 'right', px: 0.75 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: head.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {head.text}
            </Typography>
            <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary', mt: 0.4 }}>
              {head.label}
            </Typography>
          </Box>
          <KeyboardArrowRight fontSize="small" sx={{ color: 'text.secondary' }} />
        </Stack>
        <Box sx={{ mt: 1 }}>
          <RangeBar r={r} maxPos={maxPos} color={view === 'tiers' ? r.tierColor : rangeGray} zones={view === 'boom'} height={24} />
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          {tiles}
        </Stack>
      </Paper>
    );
  };
  // The axis labels sit over the cards' bars (the card's left padding + its border on each side).
  const cardAxis = (
    <Box sx={{ pl: '21px', pr: '15px', mb: 0.5 }}>
      <AxisTicks maxPos={maxPos} />
    </Box>
  );
  const cards = (
    <Stack spacing={1.5}>
      {view === 'tiers'
        ? tierGroups().map(({ t, tr }) => (
            <React.Fragment key={t}>
              {tierHeader(t, tr, true)}
              {t === tierGroups()[0].t && cardAxis}
              {tr.map(card)}
            </React.Fragment>
          ))
        : [<React.Fragment key="axis">{cardAxis}</React.Fragment>, ...list.map(card)]}
    </Stack>
  );

  // ---- the map: boom (x) vs bust (y), our likely top finishers ----
  const { top: mapRows, boom: avgBoom, bust: avgBust } = mapAverages(rows);
  const mapH = narrow ? 400 : 500;
  const pad = { l: narrow ? 40 : 48, r: 14, t: 34, b: 40 };
  const bx = Math.max(...mapRows.map((r) => r.d.top5Prob)) * 1.08;
  const by0 = Math.min(...mapRows.map((r) => r.bust)) * 0.55;
  const by1 = Math.max(...mapRows.map((r) => r.bust)) * 1.05;
  const MX = (v: number) => pad.l + (v / bx) * (width - pad.l - pad.r);
  const MY = (v: number) => pad.t + ((v - by0) / (by1 - by0)) * (mapH - pad.t - pad.b);
  const step = (span: number) => (span > 0.45 ? 0.2 : 0.1);
  const xTicks = Array.from({ length: Math.floor(bx / step(bx)) + 1 }, (_, i) => i * step(bx));
  const yTicks = Array.from({ length: 10 }, (_, i) => i * step(by1 - by0)).filter((v) => v >= by0 && v <= by1);
  // Names: favorites first, then by boom chance; each tries right, left, above and below its dot and is left off when
  // all four would cover another name or dot. The layout never depends on hover or the pick, so names stay put while
  // you move around the map; a hovered or picked driver without a name gets a floating one of its own.
  const fs = narrow ? 10.5 : 12;
  const placed: { x: number; y: number; anchor: 'start' | 'end' | 'middle'; r: FieldRow; bold: boolean }[] = [];
  {
    const boxes: number[][] = [];
    const dots = mapRows.map((r) => [MX(r.d.top5Prob), MY(r.bust)]);
    const rank = (r: FieldRow) => (favorites.has(r.d.driver) ? 0 : 1);
    const order = [...mapRows].sort((a, b) => rank(a) - rank(b) || b.d.top5Prob - a.d.top5Prob);
    for (const r of order) {
      const fav = favorites.has(r.d.driver);
      const text = `${fav ? '★ ' : ''}${lastName(r.d.driver)}`;
      const tw = text.length * fs * 0.6;
      const cx = MX(r.d.top5Prob);
      const cy = MY(r.bust);
      // clear of the picked dot's ring, wherever the pick goes
      const o = 14;
      const tries: [number, number, 'start' | 'end' | 'middle'][] = [
        [cx + o, cy + 4, 'start'],
        [cx - o, cy + 4, 'end'],
        [cx, cy - o - 1, 'middle'],
        [cx, cy + o + 9, 'middle'],
      ];
      for (const [lx, ly, a] of tries) {
        const x0 = a === 'start' ? lx : a === 'end' ? lx - tw : lx - tw / 2;
        const b = [x0 - 1, ly - fs, x0 + tw + 1, ly + 2];
        const inside = b[0] >= pad.l + 2 && b[2] <= width - 2 && b[1] >= pad.t - 6 && b[3] <= mapH - pad.b + 2;
        const hit =
          boxes.some((o) => !(b[2] < o[0] || b[0] > o[2] || b[3] < o[1] || b[1] > o[3])) ||
          dots.some(([dx, dy]) => (dx !== cx || dy !== cy) && dx > b[0] - 4 && dx < b[2] + 4 && dy > b[1] - 4 && dy < b[3] + 4);
        if (!inside || hit) continue;
        boxes.push(b);
        placed.push({ x: lx, y: ly, anchor: a, r, bold: fav });
        break;
      }
    }
  }
  const cap = { fontSize: narrow ? 10 : 11, fontWeight: 700, letterSpacing: '0.06em' };
  const nearestDot = (e: React.MouseEvent<SVGRectElement>) => {
    const b = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - b.left;
    const y = e.clientY - b.top;
    let best: string | null = null;
    let bestD = 22;
    mapRows.forEach((r) => {
      const d = Math.hypot(MX(r.d.top5Prob) - x, MY(r.bust) - y);
      if (d < bestD) {
        bestD = d;
        best = r.d.driver;
      }
    });
    return best;
  };
  const map = (
    <Box onMouseLeave={() => setHover(null)} sx={{ pt: 1 }}>
      <svg width={width} height={mapH} style={{ display: 'block' }}>
        <rect x={MX(avgBoom)} y={pad.t} width={width - pad.r - MX(avgBoom)} height={MY(avgBust) - pad.t} fill={alpha(theme.palette.success.main, 0.08)} />
        <rect x={MX(avgBoom)} y={MY(avgBust)} width={width - pad.r - MX(avgBoom)} height={mapH - pad.b - MY(avgBust)} fill={alpha(theme.palette.warning.main, 0.07)} />
        {xTicks.map((v) => (
          <g key={`x${v}`}>
            <line x1={MX(v)} x2={MX(v)} y1={pad.t} y2={mapH - pad.b} stroke={tokens.grid} />
            <text x={MX(v)} y={mapH - pad.b + 14} textAnchor="middle" fontSize={10.5} fontWeight={700} fill={tokens.axisText}>
              {Math.round(v * 100)}%
            </text>
          </g>
        ))}
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line x1={pad.l} x2={width - pad.r} y1={MY(v)} y2={MY(v)} stroke={tokens.grid} />
            <text x={pad.l - 6} y={MY(v) + 3.5} textAnchor="end" fontSize={10.5} fontWeight={700} fill={tokens.axisText}>
              {Math.round(v * 100)}%
            </text>
          </g>
        ))}
        <line x1={MX(avgBoom)} x2={MX(avgBoom)} y1={pad.t} y2={mapH - pad.b} stroke={tokens.axisText} strokeOpacity={0.5} strokeDasharray="3 4" />
        <line x1={pad.l} x2={width - pad.r} y1={MY(avgBust)} y2={MY(avgBust)} stroke={tokens.axisText} strokeOpacity={0.5} strokeDasharray="3 4" />
        <text x={pad.l + 4} y={pad.t - 12} {...cap} fill={tokens.axisText}>SAFE BUT QUIET</text>
        <text x={width - pad.r} y={pad.t - 12} textAnchor="end" {...cap} fill={theme.palette.success.main}>STEADY CONTENDERS</text>
        <text x={pad.l + 4} y={mapH - 6} {...cap} fill={tokens.axisText}>LONG SHOTS</text>
        <text x={width - pad.r} y={mapH - 6} textAnchor="end" {...cap} fill={theme.palette.warning.main}>BOOM OR BUST</text>
        <text x={(pad.l + width - pad.r) / 2} y={mapH - 6} textAnchor="middle" fontSize={10} fontWeight={700} letterSpacing="0.06em" fill={tokens.axisText}>
          {narrow ? 'BOOM' : 'BOOM CHANCE (TOP 5)'}
        </text>
        <text
          transform={`translate(${narrow ? 10 : 13} ${(mapH - pad.b + pad.t) / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize={10}
          fontWeight={700}
          letterSpacing="0.06em"
          fill={tokens.axisText}
        >
          {narrow ? 'BUST' : 'BUST CHANCE (21ST+)'}
        </text>
        {mapRows.map((r) => {
          const on = hover === r.d.driver || selected === r.d.driver;
          const cx = MX(r.d.top5Prob);
          const cy = MY(r.bust);
          return (
            <g key={r.d.driver} opacity={shown.has(r.d.driver) ? 1 : 0.3} style={{ pointerEvents: 'none' }}>
              {selected === r.d.driver && <circle cx={cx} cy={cy} r={11} fill="none" stroke={tokens.text} strokeWidth={2} />}
              <circle data-dot={r.d.driver} cx={cx} cy={cy} r={on ? 7 : 5.5} fill={r.tierColor} stroke={tokens.surface} strokeWidth={2} />
            </g>
          );
        })}
        {placed.map((l) => {
          const on = l.bold || l.r.d.driver === hover || l.r.d.driver === selected;
          return (
            <text
              key={l.r.d.driver}
              x={l.x}
              y={l.y}
              textAnchor={l.anchor}
              fontSize={fs}
              fontWeight={on ? 800 : 600}
              fill={on ? tokens.text : alpha(tokens.text, 0.75)}
              style={{ pointerEvents: 'none' }}
            >
              {favorites.has(l.r.d.driver) ? '★ ' : ''}
              {lastName(l.r.d.driver)}
            </text>
          );
        })}
        {/* One hit area for the whole plot: the pointer hovers / picks the nearest dot within reach, so crowded dots
            never cover each other's targets and a growing hovered dot can't steal the pointer. */}
        <rect
          x={0}
          y={0}
          width={width}
          height={mapH}
          fill="transparent"
          style={{ cursor: hover ? 'pointer' : 'default' }}
          onMouseMove={(e) => {
            const d = nearestDot(e);
            if (d !== hover) setHover(d);
          }}
          onClick={(e) => {
            const d = nearestDot(e);
            if (d) onSelect(d);
          }}
        />
        {/* Hovered / picked drivers whose names were left off: a floating label above the dot, over everything. */}
        {[selected, hover]
          .filter((d, i, a): d is string => !!d && a.indexOf(d) === i && !placed.some((l) => l.r.d.driver === d))
          .map((d) => {
            const r = mapRows.find((x) => x.d.driver === d);
            if (!r) return null;
            const text = `${favorites.has(d) ? '★ ' : ''}${lastName(d)}`;
            const tw = text.length * fs * 0.62 + 10;
            const cx = Math.min(width - pad.r - tw / 2, Math.max(pad.l + tw / 2, MX(r.d.top5Prob)));
            const cy = MY(r.bust) - 16;
            return (
              <g key={`float-${d}`} style={{ pointerEvents: 'none' }}>
                <rect x={cx - tw / 2} y={cy - fs - 3} width={tw} height={fs + 8} rx={4} fill={tokens.surface} stroke={tokens.grid} />
                <text x={cx} y={cy} textAnchor="middle" fontSize={fs} fontWeight={800} fill={tokens.text}>
                  {text}
                </text>
              </g>
            );
          })}
      </svg>
      {/* Desktop: the picked driver's row under the map, in the table's look (phones open the driver panel). */}
      {!narrow && selected && rows.some((r) => r.d.driver === selected) && (
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          {headerRow}
          {desktopRow(rows.find((r) => r.d.driver === selected)!)}
        </Box>
      )}
    </Box>
  );

  // ---- phones, table view: one outcome bar per driver (top-5 / 6th-20th / 21st+ shares) ----
  const outcomeBars = (
    <Box>
      {list.map((r) => {
        const picked = selected === r.d.driver;
        const mid = Math.max(0, 1 - r.d.top5Prob - r.bust);
        const fav = favorites.has(r.d.driver);
        return (
          <Stack
            key={r.d.driver}
            direction="row"
            alignItems="center"
            spacing={0.75}
            onClick={() => onSelect(r.d.driver)}
            sx={{
              px: 1.5,
              py: 0.6,
              borderTop: 1,
              borderColor: 'divider',
              cursor: 'pointer',
              opacity: shown.has(r.d.driver) ? 1 : 0.3,
              bgcolor: picked ? alpha(theme.palette.primary.main, 0.14) : undefined,
            }}
          >
            <Box
              component="span"
              role="button"
              aria-label={fav ? `Unstar ${r.d.driver}` : `Star ${r.d.driver}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(r.d.driver);
              }}
              sx={{
                display: 'flex',
                color: fav ? 'warning.main' : 'text.disabled',
                fontSize: 16,
                lineHeight: 1,
                width: 18,
              }}
            >
              {fav ? '★' : '☆'}
            </Box>
            <Typography
              sx={{
                width: 84,
                flex: 'none',
                fontSize: 12.5,
                fontWeight: picked ? 700 : 500,
                textAlign: 'right',
                pr: 0.5,
              }}
              noWrap
            >
              {`${r.d.driver[0]}. ${lastName(r.d.driver)}`}
            </Typography>
            <Stack
              direction="row"
              sx={{
                flex: 1,
                minWidth: 0,
                height: picked ? 22 : 18,
                borderRadius: 1,
                overflow: 'hidden',
                fontSize: 10.5,
                fontWeight: 800,
                color: '#0a0d0b',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <Box
                sx={{ width: `${r.d.top5Prob * 100}%`, bgcolor: 'success.main', display: 'grid', placeItems: 'center' }}
              >
                {r.d.top5Prob >= 0.15 ? pctText(r.d.top5Prob) : ''}
              </Box>
              <Box sx={{ width: `${mid * 100}%`, bgcolor: alpha(theme.palette.text.primary, 0.18) }} />
              <Box sx={{ width: `${r.bust * 100}%`, bgcolor: 'error.main', display: 'grid', placeItems: 'center' }}>
                {r.bust >= 0.15 ? pctText(r.bust) : ''}
              </Box>
            </Stack>
            <Box
              sx={{
                width: 40,
                flex: 'none',
                textAlign: 'center',
                borderRadius: 1,
                py: 0.2,
                fontSize: boomSort.key === 'avg' ? 12.5 : 11.5,
                fontWeight: boomSort.key === 'avg' ? 800 : 600,
                fontVariantNumeric: 'tabular-nums',
                bgcolor: alpha(
                  vivid(getCellColor(r.d.avgFinish, 'position')),
                  theme.palette.mode === 'dark' ? 0.58 : 0.85,
                ),
              }}
            >
              {r.d.avgFinish.toFixed(1)}
            </Box>
          </Stack>
        );
      })}
    </Box>
  );

  // ---- phones, table view: Tiers as one-line rows (star, name, range, average finish) under each tier's label ----
  const tierRows = (
    <Box>
      <Box sx={{ pl: '120px', pr: '58px', pt: 1, pb: 0.5 }}>
        <AxisTicks maxPos={maxPos} />
      </Box>
      {tierGroups().map(({ t, tr }) => (
        <React.Fragment key={t}>
          <Box sx={{ px: 1.5, py: 0.6, borderTop: 1, borderColor: 'divider', bgcolor: alpha(tr[0].tierColor, 0.1) }}>
            {tierHeader(t, tr, true)}
          </Box>
          {tr.map((r) => {
            const picked = selected === r.d.driver;
            const fav = favorites.has(r.d.driver);
            return (
              <Stack
                key={r.d.driver}
                direction="row"
                alignItems="center"
                spacing={0.75}
                onClick={() => onSelect(r.d.driver)}
                sx={{
                  px: 1.5,
                  py: 0.4,
                  borderTop: 1,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  opacity: shown.has(r.d.driver) ? 1 : 0.3,
                  bgcolor: picked ? alpha(theme.palette.primary.main, 0.14) : fav ? favTint : undefined,
                  boxShadow: `inset 3px 0 0 ${r.tierColor}`,
                }}
              >
                <Box
                  component="span"
                  role="button"
                  aria-label={fav ? `Unstar ${r.d.driver}` : `Star ${r.d.driver}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(r.d.driver);
                  }}
                  sx={{ display: 'flex', color: fav ? 'warning.main' : 'text.disabled', fontSize: 16, lineHeight: 1, width: 18 }}
                >
                  {fav ? '★' : '☆'}
                </Box>
                <Typography
                  sx={{ width: 84, flex: 'none', fontSize: 12.5, fontWeight: picked ? 700 : 500, textAlign: 'right', pr: 0.5 }}
                  noWrap
                >
                  {`${r.d.driver[0]}. ${lastName(r.d.driver)}`}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <RangeBar r={r} maxPos={maxPos} color={r.tierColor} height={22} />
                </Box>
                <Box
                  sx={{
                    width: 40,
                    flex: 'none',
                    textAlign: 'center',
                    borderRadius: 1,
                    py: 0.2,
                    fontSize: 12,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    ...positionCellSx(r.d.avgFinish, theme.palette.mode),
                  }}
                >
                  {r.d.avgFinish.toFixed(1)}
                </Box>
              </Stack>
            );
          })}
        </React.Fragment>
      ))}
    </Box>
  );

  // Phones: the cards sit on the page like the phone table's, with the toolbar above them; the map keeps its card.
  if (narrow && !isMap && !phoneBars && !phoneTierRows)
    return (
      <Box ref={ref}>
        {toolbar}
        {cards}
      </Box>
    );
  return (
    <Paper ref={ref} sx={{ overflow: 'hidden', bgcolor: tableBg }}>
      {toolbar}
      {isMap ? map : phoneBars ? outcomeBars : phoneTierRows ? tierRows : table}
    </Paper>
  );
};
